import { db } from "@/firebase/init";
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp 
} from "firebase/firestore";

/**
 * Get all marketplace listings
 * @param {number} limitCount - Maximum number of listings to fetch
 * @returns {Promise<{success: boolean, listings: Array, message: string}>}
 */
export const getMarketplaceListings = async (limitCount = 50) => {
  try {
    const listingsRef = collection(db, 'marketplace');
    const q = query(
      listingsRef, 
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'), 
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    
    const listings = [];
    querySnapshot.forEach((doc) => {
      listings.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, listings };
  } catch (error) {
    console.error('Error getting marketplace listings:', error);
    return { success: false, listings: [], message: error.message };
  }
};

/**
 * Get user's marketplace listings
 * @param {string} userId - User ID
 * @returns {Promise<{success: boolean, listings: Array, message: string}>}
 */
export const getUserListings = async (userId) => {
  try {
    const listingsRef = collection(db, 'marketplace');
    const q = query(
      listingsRef,
      where('sellerId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    const listings = [];
    querySnapshot.forEach((doc) => {
      listings.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, listings };
  } catch (error) {
    console.error('Error getting user listings:', error);
    return { success: false, listings: [], message: error.message };
  }
};

/**
 * Create a new marketplace listing
 * @param {Object} listingData - Listing data
 * @returns {Promise<{success: boolean, listingId: string, message: string}>}
 */
export const createListing = async (listingData) => {
  try {
    const listingsRef = collection(db, 'marketplace');
    const newListingRef = doc(listingsRef);
    
    const listing = {
      ...listingData,
      id: newListingRef.id,
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(newListingRef, listing);
    
    return { 
      success: true, 
      listingId: newListingRef.id,
      listing: { ...listing, createdAt: new Date(), updatedAt: new Date() }
    };
  } catch (error) {
    console.error('Error creating listing:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Update a marketplace listing
 * @param {string} listingId - Listing ID
 * @param {Object} updates - Updated fields
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const updateListing = async (listingId, updates) => {
  try {
    const listingRef = doc(db, 'marketplace', listingId);
    
    await updateDoc(listingRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating listing:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Delete a marketplace listing
 * @param {string} listingId - Listing ID
 * @param {string} userId - User ID (for authorization)
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deleteListing = async (listingId, userId) => {
  try {
    const listingRef = doc(db, 'marketplace', listingId);
    const listingDoc = await getDoc(listingRef);
    
    if (!listingDoc.exists()) {
      return { success: false, message: 'Listing not found' };
    }
    
    // Check if user is the seller
    if (listingDoc.data().sellerId !== userId) {
      return { success: false, message: 'You can only delete your own listings' };
    }
    
    await deleteDoc(listingRef);
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting listing:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Mark listing as sold
 * @param {string} listingId - Listing ID
 * @param {string} userId - User ID (for authorization)
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const markAsSold = async (listingId, userId) => {
  try {
    const listingRef = doc(db, 'marketplace', listingId);
    const listingDoc = await getDoc(listingRef);
    
    if (!listingDoc.exists()) {
      return { success: false, message: 'Listing not found' };
    }
    
    if (listingDoc.data().sellerId !== userId) {
      return { success: false, message: 'Unauthorized' };
    }
    
    await updateDoc(listingRef, {
      status: 'sold',
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error marking as sold:', error);
    return { success: false, message: error.message };
  }
};
